import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in

  const check = useCallback(async () => {
    if (!tokenStore.access && !tokenStore.refresh) {
      setUser(false);
      return;
    }
    // Retry on transient origin errors (520/5xx/network) so a momentary hiccup
    // does not log the user out. Only an explicit 401 means "not authenticated".
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch (e) {
        if (e.response?.status === 401) {
          setUser(false);
          return;
        }
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1500));
      }
    }
    // After retries still failing: keep an existing session if we had one, else show login.
    setUser((u) => (u && typeof u === "object" ? u : false));
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    if (!user) return undefined;
    const ping = () => api.post("/presence/ping").catch(() => {});
    ping();
    const iv = setInterval(ping, 30000);
    return () => clearInterval(iv);
  }, [user]);

  const login = async (email, password, endpoint = "/auth/login") => {
    try {
      const { data } = await api.post(endpoint, { email, password });
      tokenStore.set(data.access_token, data.refresh_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    tokenStore.clear();
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, login, logout, refresh: check }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
